import { Container, Graphics, Text } from 'pixi.js';
import type { BoardRecording } from '../../../game/boardMVC/BoardRecorder';
import { BoardModel } from '../../../game/boardMVC/models/board/BoardModel';
import type { BoardState } from '../../../game/boardMVC/types';
import { BoardView } from '../../../game/boardMVC/views/board/BoardView';
import { BoardViewScheduler } from '../../../game/boardMVC/models/board/ViewScheduler';
import { BoardViewFactory } from '../../../game/boardMVC/views/board/BoardViewFactory';
import { TileModel } from '../../../game/boardMVC/models/board/TileModel';

export class BoardRecordingVisualPlayback extends Container {
  private boardModel!: BoardModel;

  private scheduler!: BoardViewScheduler;

  private boardView!: BoardView;

  private currentMove = 0;

  constructor(private readonly recording: BoardRecording) {
    super();

    this.addBackground();

    this.createNavigationBar();
    this.setBoardState(recording.initState);

    this.props({
      parent: this.pixi.stage,
    });

    this.pixi.ticker.add(this.update);
    this.once('destroyed', () => {
      this.pixi.ticker.remove(this.update);
    });
  }

  private setBoardState(state: BoardState): void {
    this.boardModel?.destroy();
    this.boardView?.destroy({ children: true });

    this.scheduler = new BoardViewScheduler();

    this.boardModel = new BoardModel({
      state,
      scheduler: this.scheduler,
    });

    this.boardView = new BoardView(this.boardModel, new BoardViewFactory()).props({
      parent: this,
      x: 100,
      y: 100,
    });

    this.boardView.onAnimationFinished.connect((id) => {
      this.scheduler.finish(id);
    });
  }

  private createNavigationBar(): void {
    const navigationContainer = new Container().props({
      parent: this,
      x: 100,
      y: 1000,
    });

    this.addButton('NEXT', () => {
      const { colors, x, y } = this.recording.moves[this.currentMove];
      this.currentMove += 1;

      const stack = this.boardModel.getStack(x, y);
      if (!stack) {
        throw new Error(`cannot find stack ${x}x${y}`);
      }
      this.boardModel.makeMove(
        stack.id,
        colors.map((color) => new TileModel(color)),
      );
    }).props({
      parent: navigationContainer,
    });

    this.addButton('PREV', () => {
      if (this.currentMove <= 0) return;
      this.currentMove -= 1;
      this.setBoardState((this.recording.moves[this.currentMove - 1] ?? { output: this.recording.initState }).output);
    }).props({
      parent: navigationContainer,
      x: 200,
    });

    this.addButton('CLOSE', () => {
      this.destroy({ children: true });
    }).props({
      parent: navigationContainer,
      x: 700,
    });
  }

  private addButton(label: string, callback: () => void): Text {
    const button = new Text(label, {
      fontSize: 40,
      fill: 'black',
    });
    button.eventMode = 'static';
    button.on('pointertap', callback);
    return button;
  }

  private addBackground(): void {
    const gfx = new Graphics();
    gfx.rect(0, 0, 4000, 4000);
    gfx.fill(0x878787);
    gfx.eventMode = 'static';
    gfx.props({ parent: this });
  }

  private readonly update = (): void => {
    this.boardModel.update(this.pixi.ticker.deltaTime);
  };
}
